
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useVpsMetrics } from "@/hooks/useVpsMetrics";
import { useServerStatus } from "@/hooks/useServerStatus";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { Cpu, HardDrive, Activity, Server, Globe, Signal } from 'lucide-react';
import { Badge } from "@/components/ui/badge";

export const VpsMetrics = () => {
  const { data: metricsData } = useVpsMetrics();
  const { isOnline, whatsappConnected, telegramConnected, activeConnections, environment, timestamp } = useServerStatus();

  const latestMetrics = metricsData?.[metricsData.length - 1] || {
    cpu_usage: 0,
    memory_usage: 0,
    disk_usage: 0,
    api_response_time: 0
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Server Status</CardTitle>
            <Globe className={`h-4 w-4 ${isOnline ? 'text-green-500' : 'text-red-500'}`} />
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">
                {isOnline ? 'Online' : 'Offline'}
              </div>
              <Badge variant={isOnline ? "default" : "destructive"}>
                {environment || 'N/A'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Última atualização: {format(new Date(timestamp || Date.now()), 'HH:mm:ss')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conexões Ativas</CardTitle>
            <Signal className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeConnections}
            </div>
            <div className="flex gap-2 mt-2">
              <Badge variant={whatsappConnected ? "default" : "secondary"} className="text-xs">
                WhatsApp
              </Badge>
              <Badge variant={telegramConnected ? "default" : "secondary"} className="text-xs">
                Telegram
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">CPU Usage</CardTitle>
            <Cpu className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMetrics.cpu_usage.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Utilização atual
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Memory Usage</CardTitle>
            <Server className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMetrics.memory_usage.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              RAM utilizada
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">API Response</CardTitle>
            <Activity className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {latestMetrics.api_response_time || 0}ms
            </div>
            <p className="text-xs text-muted-foreground">
              Tempo de resposta
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Performance do VPS</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={metricsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="created_at"
                  tickFormatter={(value) => format(new Date(value), 'HH:mm:ss')}
                />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="cpu_usage" 
                  stroke="#8884d8" 
                  name="CPU Usage (%)"
                />
                <Line 
                  type="monotone" 
                  dataKey="memory_usage" 
                  stroke="#82ca9d" 
                  name="Memory Usage (%)"
                />
                <Line 
                  type="monotone" 
                  dataKey="api_response_time" 
                  stroke="#ffc658" 
                  name="API Response (ms)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
